<script lang="ts" setup>
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import type {
  AddFlightsModalInitialRoute,
  AddFligthsModalRoute,
} from '@apps/main/new-desktop/modules/addFlightsModal';
import { AddFlightsBanner } from '@apps/main/new-desktop/modules/addFlightsModal';

import type { Location } from '@libs/maps';
import { AppSvgIcon } from '@libs/ui';

interface TransportBannerProps {
  routes: AddFlightsModalInitialRoute[];
  startPositon: Location | undefined;
  endPosition: Location | undefined;
  isSmallDevice: boolean;
  city?: string;
}

const props = defineProps<TransportBannerProps>();

interface TransportBannerEmits {
  (eventName: 'close'): void;
  (eventName: 'add-flight'): void;
  (eventName: 'add-optimized-flights', value: AddFligthsModalRoute[]): void;
}
const emit = defineEmits<TransportBannerEmits>();

const statsigEventsStore = useStatsigEventsStore();

function onConfirmFlights(routes: AddFligthsModalRoute[]): void {
  statsigEventsStore.logTripOverviewFlightAdded();
  emit('add-optimized-flights', routes);
}
</script>

<template>
  <div
    class="transport-banner adding-flights"
    :class="{ mobile: props.isSmallDevice }"
  >
    <div class="transport-banner-header">
      <div class="banner-message">
        <AppSvgIcon
          name="seatInCircle"
          :size="props.isSmallDevice ? '24' : '36'"
          color="color-brand-jungle"
          aria-label="seatInCircle"
        />
        <div class="text-wrapper">
          <span class="title">
            {{ $t('overview_placeholder.transport.title', [props.city]) }}
          </span>
          <span class="description">
            {{ $t('overview_placeholder.transport.description') }}
          </span>
        </div>
      </div>
      <AppSvgIcon
        class="close-icon"
        name="close"
        size="24"
        color="color-soft-black"
        @click="emit('close')"
      />
    </div>

    <AddFlightsBanner
      :routes="props.routes"
      :start-positon="props.startPositon"
      :end-position="props.endPosition"
      @add-flights="onConfirmFlights"
    />
  </div>
</template>

<style lang="scss" scoped>
.transport-banner {
  position: relative;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr auto;
  align-items: center;
  padding: var(--spacing-16);
  overflow: hidden;
  border-radius: var(--border-radius-8);
  border: 1px dashed var(--color-neutral-30);
  background-color: var(--color-white);

  &.adding-flights {
    grid-template-columns: auto;
    grid-template-rows: 1fr auto;
    gap: var(--spacing-8);
    overflow: visible;
  }

  .transport-banner-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .close-icon {
      align-self: start;
      @include on-hover {
        cursor: pointer;
      }
    }
  }

  .banner-message {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-16);
    flex: 1;
    z-index: var(--notification-z-index);
    .text-wrapper {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8);

      .title {
        @include font-label-l-bold;
      }

      .description {
        @include font-body-m-light;
      }
    }
  }

  &.mobile {
    padding: var(--spacing-16) var(--spacing-12);
    gap: var(--spacing-16);
    grid-template-rows: auto auto;
    grid-template-columns: auto;
    .banner-message {
      @include font-label-m-bold;
      width: 100%;
      align-items: start;
      gap: var(--spacing-12);

      .text-wrapper {
        gap: var(--spacing-4);

        .title {
          @include font-label-m-bold;
        }

        .description {
          @include font-body-s-light;
        }
      }
    }
  }
}
</style>
